import { useState, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  Search,
  LayoutGrid,
  List,
  Table2,
  MoreHorizontal,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  Layers,
  Share2,
  Users,
} from "lucide-react";
import { ShareWorkspaceDialog } from "@/components/ShareWorkspaceDialog";
import { WorkspaceMembersDialog } from "@/components/WorkspaceMembersDialog";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useViewPreferences } from "@/hooks/use-view-preferences";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { workspacesService, type WorkspaceWithRole } from "@/services/workspacesService";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

type WorkspaceSortField = "name" | "createdAt" | "updatedAt";

export default function Workspaces() {
  const [, setLocation] = useLocation();
  const { workspaces, isLoadingWorkspaces, refetchWorkspaces, setCurrentWorkspaceId, currentWorkspaceId } = useWorkspace();
  const {
    viewMode,
    setViewMode,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
  } = useViewPreferences<WorkspaceSortField>({
    storageKey: "workspaces",
    defaultViewMode: "grid",
    defaultSortField: "updatedAt",
    defaultSortDirection: "desc",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [renameWorkspace, setRenameWorkspace] = useState<WorkspaceWithRole | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteWorkspace, setDeleteWorkspace] = useState<WorkspaceWithRole | null>(null);
  const [shareWorkspace, setShareWorkspace] = useState<WorkspaceWithRole | null>(null);
  const [membersWorkspace, setMembersWorkspace] = useState<WorkspaceWithRole | null>(null);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const filteredAndSorted = useMemo(() => {
    let list = workspaces;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (w) =>
          w.name.toLowerCase().includes(q) || (w.slug && w.slug.toLowerCase().includes(q))
      );
    }
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortField === "name") {
        cmp = (a.name || "").localeCompare(b.name || "");
      } else if (sortField === "createdAt") {
        cmp = new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
      } else {
        cmp = new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime();
      }
      return sortDirection === "asc" ? cmp : -cmp;
    });
    return list;
  }, [workspaces, searchQuery, sortField, sortDirection]);

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      workspacesService.updateWorkspace(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      refetchWorkspaces();
      setRenameWorkspace(null);
      setRenameValue("");
      toast({ title: "Workspace renamed", description: "The workspace name has been updated." });
    },
    onError: (err: Error) => {
      toast({ title: "Rename failed", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => workspacesService.deleteWorkspace(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      refetchWorkspaces();
      if (currentWorkspaceId === id) {
        setCurrentWorkspaceId(null);
        setLocation("/");
      }
      setDeleteWorkspace(null);
      toast({ title: "Workspace deleted", description: "The workspace has been permanently deleted." });
    },
    onError: (err: Error) => {
      toast({ title: "Delete failed", description: err.message, variant: "destructive" });
    },
  });

  const canRename = (w: WorkspaceWithRole) => w.role === "owner" || w.role === "admin";
  const canDelete = (w: WorkspaceWithRole) => w.role === "owner";
  const canShare = (w: WorkspaceWithRole) => w.role === "owner";

  const openRename = (w: WorkspaceWithRole) => {
    setRenameWorkspace(w);
    setRenameValue(w.name);
  };

  if (isLoadingWorkspaces) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading workspaces...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Workspaces</h1>
        <p className="text-muted-foreground">
          View and manage your workspaces. Owners can share a workspace with a link (editor or viewer). Switch to one or rename and delete ones you own.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap mt-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search workspaces..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>

        <Select
          value={`${sortField}-${sortDirection}`}
          onValueChange={(value) => {
            const [field, direction] = value.split("-") as [WorkspaceSortField, "asc" | "desc"];
            setSortField(field);
            setSortDirection(direction);
          }}
        >
          <SelectTrigger className="w-[140px] h-9 text-sm">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name-asc">
              <div className="flex items-center gap-2">
                <ArrowUp className="h-3 w-3" />
                <span>Name A → Z</span>
              </div>
            </SelectItem>
            <SelectItem value="name-desc">
              <div className="flex items-center gap-2">
                <ArrowDown className="h-3 w-3" />
                <span>Name Z → A</span>
              </div>
            </SelectItem>
            <SelectItem value="updatedAt-desc">
              <div className="flex items-center gap-2">
                <ArrowDown className="h-3 w-3" />
                <span>Recent</span>
              </div>
            </SelectItem>
            <SelectItem value="updatedAt-asc">
              <div className="flex items-center gap-2">
                <ArrowUp className="h-3 w-3" />
                <span>Least recent</span>
              </div>
            </SelectItem>
            <SelectItem value="createdAt-desc">
              <div className="flex items-center gap-2">
                <ArrowDown className="h-3 w-3" />
                <span>Newest</span>
              </div>
            </SelectItem>
            <SelectItem value="createdAt-asc">
              <div className="flex items-center gap-2">
                <ArrowUp className="h-3 w-3" />
                <span>Oldest</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-0.5 border rounded-md p-0.5">
          <Button
            variant={viewMode === "grid" ? "secondary" : "ghost"}
            size="icon"
            className="h-7 w-7"
            onClick={() => setViewMode("grid")}
            title="Grid view"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="icon"
            className="h-7 w-7"
            onClick={() => setViewMode("list")}
            title="List view"
          >
            <List className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant={viewMode === "table" ? "secondary" : "ghost"}
            size="icon"
            className="h-7 w-7"
            onClick={() => setViewMode("table")}
            title="Table view"
          >
            <Table2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <ScrollArea className="mt-6">
        {filteredAndSorted.length === 0 ? (
          <Card className="p-12 text-center">
            <Layers className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold mb-2">No workspaces found</h3>
            <p className="text-sm text-muted-foreground">
              {searchQuery ? "Try adjusting your search." : "Create a workspace from the sidebar to get started."}
            </p>
          </Card>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAndSorted.map((w) => (
              <Card
                key={w.id}
                className="cursor-pointer transition-colors hover:bg-accent/50"
                onClick={() => {
                  setCurrentWorkspaceId(w.id);
                  setLocation("/");
                }}
              >
                <CardContent className="p-4 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{w.name}</p>
                      <Badge variant="secondary" className="mt-1 text-xs capitalize">
                        {w.role}
                      </Badge>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canShare(w) && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setShareWorkspace(w);
                            }}
                          >
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                          </DropdownMenuItem>
                        )}
                        {canShare(w) && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setMembersWorkspace(w);
                            }}
                          >
                            <Users className="h-4 w-4 mr-2" />
                            Members
                          </DropdownMenuItem>
                        )}
                        {canRename(w) && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openRename(w); }}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                        )}
                        {canDelete(w) && (
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => { e.stopPropagation(); setDeleteWorkspace(w); }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Updated {formatDistanceToNow(new Date(w.updatedAt || 0), { addSuffix: true })}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : viewMode === "list" ? (
          <div className="border rounded-lg overflow-hidden">
            {filteredAndSorted.map((w) => (
              <div
                key={w.id}
                className="flex items-center gap-3 px-4 py-3 hover:bg-accent/50 border-b last:border-b-0 cursor-pointer"
                onClick={() => {
                  setCurrentWorkspaceId(w.id);
                  setLocation("/");
                }}
              >
                <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{w.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Updated {formatDistanceToNow(new Date(w.updatedAt || 0), { addSuffix: true })}
                  </p>
                </div>
                <Badge variant="secondary" className="capitalize shrink-0">
                  {w.role}
                </Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canShare(w) && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setShareWorkspace(w);
                        }}
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </DropdownMenuItem>
                    )}
                    {canShare(w) && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setMembersWorkspace(w);
                        }}
                      >
                        <Users className="h-4 w-4 mr-2" />
                        Members
                      </DropdownMenuItem>
                    )}
                    {canRename(w) && (
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openRename(w); }}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Rename
                      </DropdownMenuItem>
                    )}
                    {canDelete(w) && (
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => { e.stopPropagation(); setDeleteWorkspace(w); }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSorted.map((w) => (
                <TableRow
                  key={w.id}
                  className="cursor-pointer"
                  onClick={() => {
                    setCurrentWorkspaceId(w.id);
                    setLocation("/");
                  }}
                >
                  <TableCell className="font-medium">{w.name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="capitalize">
                      {w.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDistanceToNow(new Date(w.updatedAt || 0), { addSuffix: true })}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canShare(w) && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setShareWorkspace(w);
                            }}
                          >
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                          </DropdownMenuItem>
                        )}
                        {canShare(w) && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setMembersWorkspace(w);
                            }}
                          >
                            <Users className="h-4 w-4 mr-2" />
                            Members
                          </DropdownMenuItem>
                        )}
                        {canRename(w) && (
                          <DropdownMenuItem onClick={() => openRename(w)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                        )}
                        {canDelete(w) && (
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteWorkspace(w)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ScrollArea>

      {/* Rename dialog */}
      <Dialog open={!!renameWorkspace} onOpenChange={(open) => !open && setRenameWorkspace(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Workspace name"
              onKeyDown={(e) => e.key === "Enter" && renameWorkspace && renameValue.trim() && updateMutation.mutate({ id: renameWorkspace.id, name: renameValue.trim() })}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameWorkspace(null)}>
              Cancel
            </Button>
            <Button
              disabled={!renameValue.trim() || updateMutation.isPending}
              onClick={() => renameWorkspace && renameValue.trim() && updateMutation.mutate({ id: renameWorkspace.id, name: renameValue.trim() })}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ShareWorkspaceDialog
        open={!!shareWorkspace}
        onOpenChange={(open) => !open && setShareWorkspace(null)}
        workspaceId={shareWorkspace?.id ?? ""}
        workspaceName={shareWorkspace?.name ?? ""}
      />

      <WorkspaceMembersDialog
        open={!!membersWorkspace}
        onOpenChange={(open) => !open && setMembersWorkspace(null)}
        workspaceId={membersWorkspace?.id ?? ""}
        workspaceName={membersWorkspace?.name ?? ""}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteWorkspace} onOpenChange={(open) => !open && setDeleteWorkspace(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete workspace</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteWorkspace?.name}&quot;? This action cannot be undone and all workspace data will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteWorkspace && deleteMutation.mutate(deleteWorkspace.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
